import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Fingerprint, LogIn } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, errMsg } from "../lib/api";

const inp = "w-full h-11 bg-muted/60 border px-3 text-sm outline-none focus:border-primary";
const PASTEL = { background: "linear-gradient(160deg, #fff4ec 0%, #f3f0ff 55%, #ecf8f3 100%)" };

const Pitch = ({ compact }) => (
  <div className="ax-fade" data-testid="login-pitch">
    <div className={`ax-label text-primary ${compact ? "mb-2" : "mb-4"}`}>AXIOM VISUAL COMMUNICATIONS PVT LTD</div>
    <h1 className={compact ? "text-2xl font-bold tracking-tight leading-tight" : "text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"}>One Platform.<br />Complete Business Control.</h1>
    <p className={`text-muted-foreground max-w-md ${compact ? "mt-3 text-sm" : "mt-6"}`}>Manage your leads, orders, operations and business performance, all in one place.</p>
    <p className={`font-medium text-foreground ${compact ? "mt-3 text-sm" : "mt-6 text-lg"}`}>Simple. Connected. Secure.</p>
  </div>
);

export default function Login() {
  const { user, loading, refresh } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(loc.state?.error || null);
  const [busy, setBusy] = useState(false);
  if (!loading && user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/login", f);
      await refresh();
      nav("/", { replace: true });
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r relative overflow-hidden" style={PASTEL}>
        <div className="font-display font-bold text-xl">AXIOM<span className="text-primary">/</span>ONE</div>
        <Pitch />
        <div className="flex gap-6 text-xs text-muted-foreground">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Deny-by-default access</span>
          <span className="flex items-center gap-2"><Fingerprint className="h-4 w-4 text-primary" /> Every action audited</span>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm ax-fade">
          <div className="lg:hidden font-display font-bold text-xl mb-5">AXIOM<span className="text-primary">/</span>ONE</div>
          <div className="lg:hidden mb-8 rounded-2xl p-5" style={PASTEL}><Pitch compact /></div>
          <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-2">Use the email address your administrator approved. Access is by invitation only.</p>
          {err && <div data-testid="login-error" className="mt-6 border border-destructive/50 text-destructive text-sm p-3 rounded-xl">{err}</div>}
          <div className="mt-8 space-y-3">
            <input data-testid="login-email" type="email" required autoComplete="username" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} />
            <input data-testid="login-password" type="password" required autoComplete="current-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inp} />
          </div>
          <button data-testid="email-signin-button" type="submit" disabled={busy}
            className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50">
            <LogIn className="h-4 w-4" /> {busy ? "Signing in…" : "Continue with your email"}
          </button>
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
          <button type="button" data-testid="google-signin-button" onClick={google} className="w-full h-11 rounded-xl border bg-white text-sm hover:border-primary transition-colors">Continue with your @axiomviscom.in account</button>
          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">Sessions expire after 4 hours, or after 2 hours of inactivity. <a href="/forgot-password" data-testid="forgot-password-link" className="text-primary hover:underline">Forgot your password?</a></p>
        </form>
      </div>
    </div>
  );
}
